/**
 * AgentHistory.js
 * Page showing history of agent executions
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTeam } from "../context/TeamContext";
import DataService from "../services/DataService";
import { FaChartLine, FaTrash, FaEye } from "react-icons/fa";

export default function AgentHistory() {
  const { currentTeam, permissions } = useTeam();
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");

  const dataService = new DataService();

  useEffect(() => {
    if (currentTeam) {
      loadExecutions();
    }
  }, [currentTeam]);

  const loadExecutions = async () => {
    try {
      setLoading(true);
      const data = await dataService.getAgentExecutions(currentTeam.id, 100);
      setExecutions(data);
    } catch (error) {
      console.error("Error loading executions:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (executionId) => {
    if (!window.confirm("Are you sure you want to delete this execution?")) {
      return;
    }

    try {
      await dataService.deleteAgentExecution(currentTeam.id, executionId);
      await loadExecutions();
      alert("Execution deleted successfully");
    } catch (error) {
      console.error("Delete error:", error);
      alert("Failed to delete execution");
    }
  };

  const filteredExecutions = executions.filter((exec) => {
    if (filterStatus === "all") return true;
    return exec.status === filterStatus;
  });

  const getAgentTypeName = (type) => {
    switch (type) {
      case "flowchart":
        return "Flowchart";
      default:
        return type;
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "completed":
        return "status-success";
      case "failed":
        return "status-error";
      case "running":
        return "status-warning";
      default:
        return "status-default";
    }
  };

  if (loading) {
    return (
      <div className="page-loading">
        <div className="spinner"></div>
        <p>Loading history...</p>
      </div>
    );
  }

  return (
    <div className="agent-history-page">
      {/* Header */}
      <div className="page-header">
        <h1>Agent History</h1>
        <p className="page-subtitle">
          View and manage your diagram generations
        </p>
      </div>

      {/* Stats */}
      <div className="history-stats">
        <div className="stat-item">
          <div className="stat-value">{executions.length}</div>
          <div className="stat-label">Total Executions</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">
            {executions.filter((e) => e.status === "completed").length}
          </div>
          <div className="stat-label">Completed</div>
        </div>
        <div className="stat-item">
          <div className="stat-value">
            {executions.filter((e) => e.status === "failed").length}
          </div>
          <div className="stat-label">Failed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="history-filters">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Statuses</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="running">Running</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      {/* Executions List */}
      {filteredExecutions.length > 0 ? (
        <div className="executions-table">
          <table>
            <thead>
              <tr>
                <th>Agent Type</th>
                <th>Prompt</th>
                <th>Contexts</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredExecutions.map((execution) => (
                <tr key={execution.id}>
                  <td>
                    <span className="agent-type-badge">
                      {getAgentTypeName(execution.agentType)}
                    </span>
                  </td>
                  <td className="prompt-cell">
                    {execution.prompt || "Untitled"}
                  </td>
                  <td>{execution.contextIds?.length || 0}</td>
                  <td>
                    <span
                      className={`status-badge ${getStatusBadgeClass(
                        execution.status
                      )}`}
                    >
                      {execution.status}
                    </span>
                  </td>
                  <td className="date-cell">
                    {new Date(
                      execution.createdAt?.toDate?.() || execution.createdAt
                    ).toLocaleString()}
                  </td>
                  <td className="actions-cell">
                    {execution.status === "completed" && (
                      <Link
                        to={`/data-next/agents/flowchart/${execution.id}`}
                        className="btn btn-sm btn-icon"
                        title="View"
                      >
                        <FaEye />
                      </Link>
                    )}
                    {permissions.canManage && (
                      <button
                        onClick={() => handleDelete(execution.id)}
                        className="btn btn-sm btn-icon btn-danger"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h2>No Executions Yet</h2>
          <p>Start by generating your first flowchart</p>
          {permissions.canWrite && (
            <Link to="/data-next/agents/flowchart" className="btn btn-primary">
              <FaChartLine /> Generate Flowchart
            </Link>
          )}
        </div>
      )}
    </div>
  );
}
